import { yupResolver } from '@hookform/resolvers/yup';
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Radio,
  RadioGroup,
  Snackbar,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { useAppSelector } from 'app/hooks';
import React, { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { FaArrowDown, FaArrowLeft, FaCalculator, FaCheckCircle, FaArrowUp } from 'react-icons/fa';
import { MdCancel as CancelIcon, MdPerson } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import axiosInstance from 'Services/APIService';
import * as yup from 'yup';
import {
  getInvestorLandingData,
  getLandingData,
  resetUnitsSellStatus,
  selectError,
  selectInvestorLandingData,
  selectInvestorLandingDataStatus,
  selectLandingData,
  selectLandingDataStatus,
  selectLastUnitsSell,
  selectLoading,
  selectUnitsSellStatus,
  sellUnits,
  type SellUnitsPayload
} from '../../../slices/fundManagementInvestment/fundmanagementInvestmentSlice';
import { manageOperationsPercentage } from 'slices/fundManagementInvestment/fundManagementConfigurationSlice';

interface SellGrowthUnitsProps {
  onBack: () => void;
}

interface SellFormData {
  investorName: string;
  investorsEmailId: string;
  investorAvatar: string;
  calculationType: 'amount' | 'units';
  sellUnits: number;
  unitPrice: number;
  totalAmount: number;
}

interface InvestorLandingDataType {
  incomeUnits?: {
    totalUnitsHoldTillDate: string | number;
    unitPrice?: string | number;
  };
  growthUnits?: {
    totalUnitsHoldTillDate: string | number;
    unitPrice?: string | number;
  };
}

const validationSchema = yup.object({
  investorName: yup
    .string()
    .required('Investor name is required')
    .min(2, 'Name must be at least 2 characters'),
  investorsEmailId: yup
    .string()
    .required('Email address is required')
    .email('Please enter a valid email address'),
  investorAvatar: yup.string().required('Avatar is required'),
  calculationType: yup
    .string()
    .oneOf(['amount', 'units'], 'Invalid calculation type')
    .required('Calculation type is required'),
  sellUnits: yup.number().positive('Units must be positive').required('Units are required'),
  totalAmount: yup.number().positive('Amount must be positive').required('Amount is required'),
  unitPrice: yup.number().positive('Unit price must be positive').required('Unit price is required')
});

const SellGrowthUnits: React.FC<SellGrowthUnitsProps> = ({ onBack }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const sellStatus = useSelector(selectUnitsSellStatus);
  const loading = useSelector(selectLoading);
  const error = useSelector(selectError);
  const lastTransaction = useSelector(selectLastUnitsSell);
  const investorLandingData = useSelector(selectInvestorLandingData) as InvestorLandingDataType;
  const investorLandingDataStatus = useSelector(selectInvestorLandingDataStatus);
  const landingData = useSelector(selectLandingData);
  const landingDataStatus = useSelector(selectLandingDataStatus);

  const selectedProjectId = useAppSelector(
    (state) => state.homeTilesServices.selectedProjectId || ''
  );

  const [loggedInUser, setLoggedInUser] = useState(() => {
    const loginInfo = localStorage.getItem('user-details');
    if (loginInfo) {
      try {
        const userDetails = JSON.parse(loginInfo);
        return {
          name: userDetails.name || '',
          email: userDetails.email || '',
          avatar: userDetails.avatar || ''
        };
      } catch (error) {
        console.error('Error parsing user details:', error);
        return { name: '', email: '', avatar: '' };
      }
    }
    return { name: '', email: '', avatar: '' };
  });

  const [loggedUserAvatarUrl, setLoggedUserAvatarUrl] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info'
  });

  const [operationalPercentage, setOperationalPercentage] = useState<number>(0);
  const [feeLoadingStatus, setFeeLoadingStatus] = useState<
    'idle' | 'loading' | 'success' | 'error'
  >('idle');

  const getGroupId = () => {
    const selectedProject = JSON.parse(localStorage.getItem('selectedProject') || '{}') as {
      projectId?: string;
    };
    return selectedProject?.projectId || '';
  };

  const getSellQueueFriendlyMessage = (message: string) => {
    if (message.toLowerCase().includes('you already have an open sell request')) {
      return 'Request is in queue. Please wait for admin to review.';
    }
    return message;
  };

  useEffect(() => {
    const fetchLoggedUserAvatar = async () => {
      if (loggedInUser.avatar) {
        try {
          const response = await axiosInstance.get(
            `/avatar/get-avatar?path=${encodeURIComponent(loggedInUser.avatar)}`,
            { responseType: 'blob' }
          );
          const imageUrl = URL.createObjectURL(response.data);
          setLoggedUserAvatarUrl(imageUrl);
        } catch (error) {
          console.error(`Error fetching logged user avatar:`, error);
          setLoggedUserAvatarUrl(null);
        }
      }
    };

    fetchLoggedUserAvatar();

    return () => {
      if (loggedUserAvatarUrl) {
        URL.revokeObjectURL(loggedUserAvatarUrl);
      }
    };
  }, [loggedInUser.avatar]);

  useEffect(() => {
    const loadOperationalPercentage = async () => {
      try {
        const projectId = selectedProjectId || getGroupId();
        setFeeLoadingStatus('loading');

        const result = await dispatch(
          manageOperationsPercentage({
            action: 'get',
            groupId: projectId,
            serviceId: 'fundManagementInvestment',
            unitType: 'growthUnits',
            perType: 'sell'
          }) as any
        );

        if (result.payload?.response?.data?.percentage !== undefined) {
          const percentage = result.payload.response.data.percentage;
          setOperationalPercentage(percentage);
          setFeeLoadingStatus('success');
          console.log(`Operational fee for sell growthUnits: ${percentage}%`);
        } else {
          setFeeLoadingStatus('error');
        }
      } catch (error) {
        console.error('Error loading operational percentage:', error);
        setFeeLoadingStatus('error');
        setOperationalPercentage(0);
      }
    };

    loadOperationalPercentage();
  }, [dispatch, selectedProjectId]);

  const growthUnitsData = useMemo(() => {
    let unitPrice = 0;
    let totalUnitsHold = 0;

    if (
      investorLandingDataStatus === 'succeeded' &&
      investorLandingData &&
      investorLandingData.growthUnits
    ) {
      totalUnitsHold =
        parseFloat(String(investorLandingData.growthUnits.totalUnitsHoldTillDate)) || 0;

      // Fallback to investor payload unit price when admin landing data is unavailable.
      unitPrice = parseFloat(String(investorLandingData.growthUnits.unitPrice ?? 0)) || 0;
    }

    if (landingDataStatus === 'succeeded' && landingData && Array.isArray(landingData)) {
      const growthData = landingData.find((data: any) => data.unitType === 'growthUnits');
      if (growthData) {
        unitPrice = parseFloat(growthData.unitPrice) || 0;
      }
    }

    const totalNAV = totalUnitsHold * unitPrice;

    return {
      totalUnitsHold,
      unitPrice,
      totalNAV
    };
  }, [investorLandingData, investorLandingDataStatus, landingData, landingDataStatus]);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isValid },
    reset
  } = useForm<SellFormData>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      investorName: loggedInUser.name,
      investorsEmailId: loggedInUser.email,
      investorAvatar: loggedInUser.avatar,
      calculationType: 'units',
      sellUnits: 0,
      unitPrice: growthUnitsData.unitPrice,
      totalAmount: 0
    },
    mode: 'onChange'
  });

  const watchedValues = watch();

  useEffect(() => {
    if (growthUnitsData.unitPrice > 0) {
      setValue('unitPrice', growthUnitsData.unitPrice);
    }
  }, [growthUnitsData.unitPrice, setValue]);

  const formatCurrency = (amount: number) => {
    if (typeof amount !== 'number' || isNaN(amount)) {
      return '$0';
    }

    const absAmount = Math.abs(amount);
    const isNegative = amount < 0;

    let formattedAmount;

    if (absAmount >= 1000000000) {
      formattedAmount = (absAmount / 1000000000).toFixed(3) + 'B';
    } else if (absAmount >= 1000000) {
      formattedAmount = (absAmount / 1000000).toFixed(3) + 'M';
    } else if (absAmount >= 1000) {
      formattedAmount = (absAmount / 1000).toFixed(3) + 'K';
    } else {
      formattedAmount = absAmount % 1 === 0 ? absAmount.toString() : absAmount.toFixed(3);
    }

    if (formattedAmount.includes('.')) {
      formattedAmount = formattedAmount.replace(/\.?0+$/, '');
    }

    const sign = isNegative ? '-' : '';
    return `${sign}$${formattedAmount}`;
  };

  const operationCharge = useMemo(() => {
    const baseAmount = watchedValues.totalAmount || 0;
    return parseFloat((baseAmount * (operationalPercentage / 100)).toFixed(2));
  }, [watchedValues.totalAmount, operationalPercentage]);

  const netAmount = useMemo(() => {
    const baseAmount = watchedValues.totalAmount || 0;
    return parseFloat((baseAmount - operationCharge).toFixed(2));
  }, [watchedValues.totalAmount, operationCharge]);

  const handleCalculationTypeChange = (type: 'amount' | 'units') => {
    setValue('calculationType', type);

    if (type === 'amount') {
      const currentAmount = watchedValues.totalAmount || 0;
      const calculatedUnits = currentAmount / growthUnitsData.unitPrice;
      setValue('sellUnits', calculatedUnits);
    } else {
      const currentUnits = watchedValues.sellUnits || 0;
      const calculatedAmount = currentUnits * growthUnitsData.unitPrice;
      setValue('totalAmount', calculatedAmount);
    }
  };

  const handleAmountChange = (amount: number) => {
    const calculatedUnits = amount / growthUnitsData.unitPrice;
    setValue('sellUnits', calculatedUnits);
    setValue('totalAmount', amount);
  };

  const handleUnitsChange = (units: number) => {
    const calculatedAmount = units * growthUnitsData.unitPrice;
    setValue('totalAmount', calculatedAmount);
    setValue('sellUnits', units);
  };

  const checkUnitsAvailable = (requestedUnits: number): boolean => {
    return requestedUnits <= growthUnitsData.totalUnitsHold && requestedUnits > 0;
  };

  const onSubmit = async (data: SellFormData) => {
    const requestedUnits = data.sellUnits;

    if (!checkUnitsAvailable(requestedUnits)) {
      const shortfall = requestedUnits - growthUnitsData.totalUnitsHold;
      setSnackbar({
        open: true,
        message: `Insufficient Units! You are trying to sell ${requestedUnits.toFixed(
          2
        )} units but only have ${growthUnitsData.totalUnitsHold.toFixed(
          2
        )} units. You need ${shortfall.toFixed(2)} more units.`,
        severity: 'error'
      });
      return;
    }

    const payload: SellUnitsPayload = {
      groupId: selectedProjectId,
      serviceId: 'fundManagementInvestment',
      investorsEmailId: data.investorsEmailId,
      investorName: data.investorName,
      investorAvatar: data.investorAvatar,
      sellUnits: data.sellUnits.toFixed(2),
      unitType: 'growthUnits',
      unitPrice: growthUnitsData.unitPrice.toFixed(2),
      operationCharge: operationCharge.toFixed(2),
      operationalPercentage: operationalPercentage,
      netAmount: netAmount.toFixed(2)
    };

    console.log('Submitting sell payload:', payload);

    try {
      const result = await dispatch(sellUnits(payload) as any);

      if (sellUnits.fulfilled.match(result)) {
        setSnackbar({
          open: true,
          message: 'Sell request submitted successfully!',
          severity: 'success'
        });

        setTimeout(() => {
          navigate('/fundManagementInvestment/request', {
            state: {
              from: 'unitSell',
              to: 'growthUnits',
              isAdmin: true
            }
          });

          reset({
            investorName: loggedInUser.name,
            investorsEmailId: loggedInUser.email,
            investorAvatar: loggedInUser.avatar,
            calculationType: 'units',
            sellUnits: 0,
            unitPrice: growthUnitsData.unitPrice,
            totalAmount: 0
          });
          dispatch(resetUnitsSellStatus());
        }, 2000);
      } else {
        const rawErrorMessage =
          (result.payload as string) ||
          (result.error as any)?.message ||
          'Sell request submission failed';
        const errorMessage = getSellQueueFriendlyMessage(rawErrorMessage);

        setSnackbar({
          open: true,
          message: errorMessage,
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Sell request submission failed:', error);
      setSnackbar({
        open: true,
        message: 'Sell request submission failed. Please try again.',
        severity: 'error'
      });
    }
  };

  React.useEffect(() => {
    if (sellStatus === 'succeeded' && lastTransaction) {
      setSnackbar({
        open: true,
        message: 'Sell request submitted successfully!',
        severity: 'success'
      });

      setTimeout(() => {
        navigate('/fundManagementInvestment/request', {
          state: {
            from: 'unitSell',
            to: 'growthUnits',
            isAdmin: true
          }
        });

        reset({
          investorName: loggedInUser.name,
          investorsEmailId: loggedInUser.email,
          investorAvatar: loggedInUser.avatar,
          calculationType: 'units',
          sellUnits: 0,
          unitPrice: growthUnitsData.unitPrice,
          totalAmount: 0
        });
        dispatch(resetUnitsSellStatus());
      }, 2000);
    } else if (sellStatus === 'failed' && error) {
      setSnackbar({
        open: true,
        message: getSellQueueFriendlyMessage(error),
        severity: 'error'
      });
    }
  }, [
    sellStatus,
    lastTransaction,
    error,
    reset,
    dispatch,
    loggedInUser,
    growthUnitsData.unitPrice,
    navigate
  ]);

  if (
    loading ||
    investorLandingDataStatus === 'idle' ||
    investorLandingDataStatus === 'pending' ||
    landingDataStatus === 'idle' ||
    landingDataStatus === 'pending'
  ) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '50vh'
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress size={40} />
          <Typography variant="body1" color="text.secondary">
            Loading growth units data...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (growthUnitsData.totalUnitsHold <= 0) {
    return (
      <Container maxWidth="xl" sx={{ padding: 3 }}>
        <IconButton onClick={onBack} sx={{ mb: 2 }}>
          <FaArrowLeft />
        </IconButton>
        <Box sx={{ textAlign: 'center', padding: 4 }}>
          <Alert severity="info" sx={{ marginBottom: 2 }}>
            You don't have any Growth Units to sell. Purchase units first to start selling.
          </Alert>
          <Button
            variant="contained"
            onClick={() => navigate('/fundManagementInvestment/purchase')}
            sx={{ textTransform: 'none' }}
          >
            Purchase Growth Units
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ padding: { xs: 1, sm: 2, md: 3 } }}>
      <Box sx={{ mb: 2 }}>
        <Button
          startIcon={<FaArrowLeft />}
          onClick={onBack}
          sx={{
            textTransform: 'none',
            color: '#666',
            '&:hover': { backgroundColor: '#f5f5f5' }
          }}
        >
          Back to Units Selection
        </Button>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Avatar
          sx={{
            width: 48,
            height: 48,
            background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
            mr: 2
          }}
        >
          <FaArrowUp size={24} />
        </Avatar>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Sell Growth Units
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            Submit a request to sell your growth units
          </Typography>
        </Box>
      </Box>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={{ xs: 2, md: 4 }}>
          <Grid item xs={12} md={4}>
            <Paper
              sx={{
                padding: { xs: 2, md: 3 },
                borderRadius: 3,
                position: { xs: 'static', md: 'sticky' },
                top: 150,
                height: 'fit-content'
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 500,
                  marginBottom: 2,
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: { xs: '0.85rem', md: '0.9rem' }
                }}
              >
                <FaArrowUp size={isMobile ? 14 : 16} color="#1976d2" style={{ marginRight: 8 }} />
                Growth Units - Summary
              </Typography>

              <Box
                sx={{
                  background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                  padding: { xs: 1.5, md: 2 },
                  borderRadius: 2,
                  color: 'white',
                  marginBottom: 3
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 500,
                    fontSize: { xs: '0.9rem', md: '1rem' },
                    marginBottom: 1
                  }}
                >
                  Growth Units
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    fontSize: { xs: '1.1rem', md: '1.2rem' }
                  }}
                >
                  {formatCurrency(growthUnitsData.unitPrice)}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    opacity: 0.9,
                    fontSize: { xs: '0.7rem', md: '0.75rem' }
                  }}
                >
                  per unit
                </Typography>
              </Box>

              <Divider sx={{ marginY: 2 }} />
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  marginBottom: 2,
                  fontSize: { xs: '0.75rem', md: '0.8rem' }
                }}
              >
                Your Holdings
              </Typography>
              <Box sx={{ marginBottom: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{ color: '#666', fontSize: { xs: '0.7rem', md: '0.75rem' } }}
                  >
                    Total Units
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, fontSize: { xs: '0.7rem', md: '0.75rem' } }}
                  >
                    {growthUnitsData.totalUnitsHold.toFixed(2)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{ color: '#666', fontSize: { xs: '0.7rem', md: '0.75rem' } }}
                  >
                    Total Value
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, fontSize: { xs: '0.7rem', md: '0.75rem' } }}
                  >
                    {formatCurrency(growthUnitsData.totalNAV)}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ marginY: 2 }} />
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  marginBottom: 2,
                  fontSize: { xs: '0.75rem', md: '0.8rem' }
                }}
              >
                Sell Summary
              </Typography>
              <Box sx={{ marginBottom: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{ color: '#666', fontSize: { xs: '0.7rem', md: '0.75rem' } }}
                  >
                    Units to Sell
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, fontSize: { xs: '0.7rem', md: '0.75rem' } }}
                  >
                    {watchedValues.sellUnits?.toFixed(2) || '0.00'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{ color: '#666', fontSize: { xs: '0.7rem', md: '0.75rem' } }}
                  >
                    Unit Price
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, fontSize: { xs: '0.7rem', md: '0.75rem' } }}
                  >
                    {formatCurrency(growthUnitsData.unitPrice)}
                  </Typography>
                </Box>
                <Divider sx={{ marginY: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, fontSize: { xs: '0.75rem', md: '0.8rem' } }}
                  >
                    Gross Amount
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: '#1976d2',
                      fontSize: { xs: '0.75rem', md: '0.8rem' }
                    }}
                  >
                    {formatCurrency(watchedValues.totalAmount || 0)}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    marginBottom: 2,
                    padding: '12px',
                    backgroundColor: '#fff3e0',
                    borderRadius: 2
                  }}
                >
                  {feeLoadingStatus === 'loading' ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={16} />
                      <Typography variant="body2" sx={{ color: '#999', fontSize: '0.8rem' }}>
                        Loading fee...
                      </Typography>
                    </Box>
                  ) : feeLoadingStatus === 'error' ? (
                    <Alert severity="warning" sx={{ padding: '4px 8px', fontSize: '0.75rem' }}>
                      Unable to load operational fee
                    </Alert>
                  ) : (
                    <>
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{ fontWeight: 'medium', marginBottom: 0.5, fontSize: '0.8rem' }}
                      >
                        Operational Fee ({operationalPercentage}%):{' '}
                        {formatCurrency(operationCharge)}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 'bold',
                          marginTop: 0.5,
                          color: '#d32f2f',
                          fontSize: { xs: '0.85rem', md: '0.9rem' }
                        }}
                      >
                        Net Amount: {formatCurrency(netAmount)}
                      </Typography>
                    </>
                  )}
                </Box>
              </Box>

              <Box
                sx={{
                  padding: 1.5,
                  backgroundColor: checkUnitsAvailable(watchedValues.sellUnits)
                    ? '#e8f5e9'
                    : '#ffebee',
                  borderRadius: 2,
                  border: `1px solid ${
                    checkUnitsAvailable(watchedValues.sellUnits) ? '#4caf50' : '#f44336'
                  }`,
                  marginTop: 2
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    marginBottom: 1,
                    fontSize: { xs: '0.7rem', md: '0.75rem' }
                  }}
                >
                  After Sell
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography
                    variant="body2"
                    sx={{ color: '#666', fontSize: { xs: '0.65rem', md: '0.7rem' } }}
                  >
                    Remaining Units
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: checkUnitsAvailable(watchedValues.sellUnits) ? '#2e7d32' : '#c62828',
                      fontSize: { xs: '0.65rem', md: '0.7rem' }
                    }}
                  >
                    {(growthUnitsData.totalUnitsHold - (watchedValues.sellUnits || 0)).toFixed(2)}
                  </Typography>
                </Box>

                {!checkUnitsAvailable(watchedValues.sellUnits) && watchedValues.sellUnits > 0 && (
                  <Alert
                    severity="error"
                    sx={{
                      marginTop: 1.5,
                      padding: '4px 8px',
                      '& .MuiAlert-message': { fontSize: { xs: '0.65rem', md: '0.7rem' } }
                    }}
                  >
                    You cannot sell more units than you hold!
                  </Alert>
                )}
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={8}>
            <Paper sx={{ padding: { xs: 2, md: 4 }, borderRadius: 3 }}>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 500,
                  marginBottom: 2,
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: { xs: '0.9rem', md: '1rem' }
                }}
              >
                <MdPerson size={isMobile ? 16 : 18} color="#1976d2" style={{ marginRight: 8 }} />
                Investor Information
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: 2,
                  backgroundColor: '#fff3e0',
                  borderRadius: 2,
                  border: '1px solid #ff9800',
                  marginBottom: 3
                }}
              >
                <Avatar
                  src={loggedUserAvatarUrl || undefined}
                  sx={{ width: { xs: 40, md: 48 }, height: { xs: 40, md: 48 }, marginRight: 2 }}
                >
                  {loggedInUser.name?.charAt(0) || 'U'}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 500, fontSize: { xs: '0.85rem', md: '0.9rem' } }}
                  >
                    {loggedInUser.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: '#666', fontSize: { xs: '0.75rem', md: '0.8rem' } }}
                  >
                    {loggedInUser.email}
                  </Typography>
                </Box>
              </Box>

              <Controller
                name="investorName"
                control={control}
                render={({ field }) => <input type="hidden" {...field} />}
              />
              <Controller
                name="investorsEmailId"
                control={control}
                render={({ field }) => <input type="hidden" {...field} />}
              />
              <Controller
                name="investorAvatar"
                control={control}
                render={({ field }) => <input type="hidden" {...field} />}
              />
              <Controller
                name="unitPrice"
                control={control}
                render={({ field }) => <input type="hidden" {...field} />}
              />

              <Divider sx={{ marginY: 3 }} />
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 500,
                  marginBottom: 2,
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: { xs: '0.85rem', md: '0.9rem' }
                }}
              >
                <FaCalculator
                  size={isMobile ? 14 : 16}
                  color="#1976d2"
                  style={{ marginRight: 8 }}
                />
                Sell Calculation
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Controller
                    name="calculationType"
                    control={control}
                    render={({ field: { onChange, value, name } }) => (
                      <FormControl component="fieldset" disabled={Boolean(loading)}>
                        <FormLabel
                          component="legend"
                          sx={{ fontSize: { xs: '0.75rem', md: '0.8rem' } }}
                        >
                          Calculate by
                        </FormLabel>
                        <RadioGroup
                          row
                          value={value}
                          onChange={(event) => {
                            const newValue = event.target.value as 'amount' | 'units';
                            onChange(newValue);
                            handleCalculationTypeChange(newValue);
                          }}
                          name={name}
                        >
                          <FormControlLabel
                            value="units"
                            control={<Radio />}
                            label={
                              <Typography sx={{ fontSize: { xs: '0.75rem', md: '0.8rem' } }}>
                                Number of Units
                              </Typography>
                            }
                          />
                          <FormControlLabel
                            value="amount"
                            control={<Radio />}
                            label={
                              <Typography sx={{ fontSize: { xs: '0.75rem', md: '0.8rem' } }}>
                                Amount to Receive
                              </Typography>
                            }
                          />
                        </RadioGroup>
                      </FormControl>
                    )}
                  />
                </Grid>

                {watchedValues.calculationType === 'units' ? (
                  <>
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name="sellUnits"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Units to Sell"
                            type="number"
                            variant="outlined"
                            error={
                              !!errors.sellUnits ||
                              (!checkUnitsAvailable(field.value) && field.value > 0)
                            }
                            helperText={
                              !checkUnitsAvailable(field.value) && field.value > 0
                                ? `Exceeds holdings by ${(
                                    field.value - growthUnitsData.totalUnitsHold
                                  ).toFixed(2)} units`
                                : errors.sellUnits?.message ||
                                  `Max: ${growthUnitsData.totalUnitsHold.toFixed(2)} units`
                            }
                            inputProps={{
                              step: '0.01',
                              min: 0,
                              max: growthUnitsData.totalUnitsHold
                            }}
                            InputProps={{
                              sx: {
                                backgroundColor:
                                  !checkUnitsAvailable(field.value) && field.value > 0
                                    ? '#ffebee'
                                    : 'transparent'
                              }
                            }}
                            disabled={Boolean(loading)}
                            size={isMobile ? 'small' : 'medium'}
                            onChange={(e) => {
                              const value = parseFloat(e.target.value) || 0;
                              field.onChange(value);
                              handleUnitsChange(value);
                            }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name="totalAmount"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Calculated Amount"
                            type="number"
                            variant="outlined"
                            error={!!errors.totalAmount}
                            helperText={errors.totalAmount?.message || 'Amount in AUD'}
                            InputProps={{
                              startAdornment: <InputAdornment position="start">AUD</InputAdornment>
                            }}
                            disabled={true}
                            size={isMobile ? 'small' : 'medium'}
                          />
                        )}
                      />
                    </Grid>
                  </>
                ) : (
                  <>
                    <Grid item xs={12} sm={6}>
                      <Controller
                        name="totalAmount"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Amount to Receive"
                            type="number"
                            variant="outlined"
                            error={!!errors.totalAmount}
                            helperText={errors.totalAmount?.message || 'Amount in AUD'}
                            InputProps={{
                              startAdornment: <InputAdornment position="start">AUD</InputAdornment>
                            }}
                            disabled={Boolean(loading)}
                            size={isMobile ? 'small' : 'medium'}
                            onChange={(e) => {
                              const value = parseFloat(e.target.value) || 0;
                              field.onChange(value);
                              handleAmountChange(value);
                            }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Calculated Units"
                        value={watchedValues.sellUnits?.toFixed(2) || '0.00'}
                        disabled
                        size={isMobile ? 'small' : 'medium'}
                        sx={{
                          '& .MuiInputBase-input.Mui-disabled': {
                            WebkitTextFillColor: '#d32f2f',
                            fontWeight: 500
                          }
                        }}
                      />
                    </Grid>
                  </>
                )}

                <Grid item xs={12}>
                  <Box sx={{ marginTop: 3, textAlign: 'center' }}>
                    <Alert
                      severity="info"
                      sx={{
                        marginBottom: 3,
                        '& .MuiAlert-message': { fontSize: { xs: '0.75rem', md: '0.8rem' } }
                      }}
                    >
                      Sell request for <strong>{loggedInUser.name}</strong> will be reviewed by the
                      fund manager.
                    </Alert>

                    {sellStatus === 'succeeded' && lastTransaction && (
                      <Alert severity="success" sx={{ marginBottom: 3 }} icon={<FaCheckCircle />}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 500, marginBottom: 1, color: '#2e7d32' }}
                        >
                          Sell Request Submitted Successfully!
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block' }}>
                          Your sell request is being processed by the fund manager.
                        </Typography>
                      </Alert>
                    )}

                    {!checkUnitsAvailable(watchedValues.sellUnits) &&
                      watchedValues.sellUnits > 0 && (
                        <Alert
                          severity="error"
                          sx={{
                            marginBottom: 3,
                            border: '2px solid #d32f2f',
                            backgroundColor: '#ffebee',
                            '& .MuiAlert-message': { width: '100%' }
                          }}
                          icon={<CancelIcon />}
                        >
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 600,
                              color: '#c62828',
                              fontSize: { xs: '0.9rem', md: '1rem' },
                              marginBottom: 1
                            }}
                          >
                            Insufficient Units
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ fontSize: { xs: '0.75rem', md: '0.8rem' }, marginBottom: 1 }}
                          >
                            You cannot sell more units than you currently hold.
                          </Typography>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              flexWrap: 'wrap',
                              gap: 1,
                              marginTop: 1
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' } }}
                            >
                              You Hold: {growthUnitsData.totalUnitsHold.toFixed(2)} units
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' } }}
                            >
                              Trying to Sell: {watchedValues.sellUnits.toFixed(2)} units
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' }, color: '#c62828' }}
                            >
                              Shortfall:{' '}
                              {(watchedValues.sellUnits - growthUnitsData.totalUnitsHold).toFixed(
                                2
                              )}{' '}
                              units
                            </Typography>
                          </Box>
                        </Alert>
                      )}

                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      disabled={
                        !isValid ||
                        Boolean(loading) ||
                        !checkUnitsAvailable(watchedValues.sellUnits)
                      }
                      startIcon={loading ? <CircularProgress size={20} /> : <FaArrowDown />}
                      sx={{
                        backgroundColor: !checkUnitsAvailable(watchedValues.sellUnits)
                          ? '#d32f2f'
                          : '#ff9800',
                        '&:hover': {
                          backgroundColor: !checkUnitsAvailable(watchedValues.sellUnits)
                            ? '#d32f2f'
                            : '#f57c00'
                        },
                        '&.Mui-disabled': {
                          backgroundColor: '#bdbdbd',
                          color: '#fff',
                          opacity: 0.7
                        },
                        textTransform: 'none',
                        fontWeight: 500,
                        borderRadius: 2,
                        padding: { xs: '10px 24px', md: '12px 36px' },
                        fontSize: { xs: '0.8rem', md: '0.9rem' }
                      }}
                    >
                      {loading
                        ? 'Submitting...'
                        : !checkUnitsAvailable(watchedValues.sellUnits)
                        ? 'Reduce Units'
                        : 'Submit Sell Request'}
                    </Button>

                    {!checkUnitsAvailable(watchedValues.sellUnits) &&
                      watchedValues.sellUnits > 0 && (
                        <Typography
                          variant="caption"
                          color="error"
                          sx={{
                            display: 'block',
                            marginTop: 2,
                            textAlign: 'center',
                            fontSize: { xs: '0.7rem', md: '0.75rem' },
                            fontWeight: 600
                          }}
                        >
                          You need to reduce sell units by{' '}
                          {(watchedValues.sellUnits - growthUnitsData.totalUnitsHold).toFixed(2)} to
                          complete this request
                        </Typography>
                      )}
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </form>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%', borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default SellGrowthUnits;
